// @cursor-model cursor-small
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getUserDecks, deleteDeck, removeUserDeck, Deck, updateClassDeckPublicFlag } from '../services/deckService';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Tab,
  Tabs,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ClassIcon from '@mui/icons-material/Class';
import StarIcon from '@mui/icons-material/Star';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase.config';

// Расширяем существующий тип Deck для наших нужд
interface ExtendedDeck extends Deck {
  fromClassId?: string;
  fromClassName?: string;
}

const DeckLibraryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [decks, setDecks] = useState<ExtendedDeck[]>([]);
  const [ownDecks, setOwnDecks] = useState<ExtendedDeck[]>([]);
  const [addedDecks, setAddedDecks] = useState<ExtendedDeck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDeckDialogOpen, setIsDeleteDeckDialogOpen] = useState(false);
  const [isRemoveDeckDialogOpen, setIsRemoveDeckDialogOpen] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState<{id: string, title: string} | null>(null);
  const [deckToRemove, setDeckToRemove] = useState<{id: string, title: string} | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchDecks = async () => {
      if (user) {
        console.log("[DeckLibraryPage] Fetching decks for user:", user.uid);
        const userDecks = await getUserDecks(user.uid);
        console.log("[DeckLibraryPage] Retrieved decks:", userDecks);
        
        // Обновляем флаги публичности для колод из классов
        for (const deck of userDecks) {
          if (deck.fromClassId) {
            await updateClassDeckPublicFlag(user.uid, deck.id);
          }
        }
        
        // Получаем обновленные колоды после исправления флагов
        const updatedDecks = await getUserDecks(user.uid);
        
        // Разделяем колоды на собственные и добавленные
        const own = updatedDecks.filter(deck => deck.userId === user.uid);
        const added = updatedDecks.filter(deck => deck.userId !== user.uid);
        
        setDecks(updatedDecks);
        setOwnDecks(own);
        setAddedDecks(added);
        setIsLoading(false);
      }
    };

    fetchDecks();
  }, [user, navigate]);

  const handleOpenDeleteDeckDialog = (deckId: string, deckTitle: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeckToDelete({ id: deckId, title: deckTitle });
    setIsDeleteDeckDialogOpen(true);
  };

  const handleCloseDeleteDeckDialog = () => {
    setIsDeleteDeckDialogOpen(false);
    setDeckToDelete(null);
  };

  const handleOpenRemoveDeckDialog = (deckId: string, deckTitle: string, fromClassId: string | undefined, fromClassName: string | undefined, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Если колода из класса, показываем информационное уведомление
    if (fromClassId && fromClassName) {
      setSnackbar({
        open: true,
        message: t('decks.deckFromClassRemoveInfo', { className: fromClassName }),
        severity: 'info',
      });
      return;
    }
    
    setDeckToRemove({ id: deckId, title: deckTitle });
    setIsRemoveDeckDialogOpen(true);
  };

  const handleCloseRemoveDeckDialog = () => {
    setIsRemoveDeckDialogOpen(false);
    setDeckToRemove(null);
  };

  const handleDeleteDeck = async () => {
    if (!deckToDelete) return;
    
    try {
      await deleteDeck(deckToDelete.id);
      setDecks(decks.filter(deck => deck.id !== deckToDelete.id));
      setOwnDecks(ownDecks.filter(deck => deck.id !== deckToDelete.id));
      setSnackbar({
        open: true,
        message: t('messages.deckDeleted'),
        severity: 'success',
      });
      handleCloseDeleteDeckDialog();
    } catch (error) {
      console.error('Error deleting deck:', error);
      setSnackbar({
        open: true,
        message: t('messages.actionFailed'),
        severity: 'error',
      });
    }
  };

  const handleRemoveDeck = async () => {
    if (!deckToRemove || !user) return;
    
    try {
      // Удаляем колоду из библиотеки пользователя (не удаляем саму колоду)
      await removeUserDeck(user.uid, deckToRemove.id);
      
      // Обновляем состояние
      setDecks(decks.filter(deck => deck.id !== deckToRemove.id));
      setAddedDecks(addedDecks.filter(deck => deck.id !== deckToRemove.id));
      
      // Уменьшаем счетчик популярности колоды, если она публичная
      try {
        const deckRef = doc(db, 'decks', deckToRemove.id);
        const deckDoc = await getDoc(deckRef);
        if (deckDoc.exists()) {
          const deckData = deckDoc.data();
          if (deckData.isPublic && typeof deckData.stats?.popularity === 'number' && deckData.stats.popularity > 0) {
            await updateDoc(deckRef, {
              'stats.popularity': deckData.stats.popularity - 1
            });
          }
        }
      } catch (error) {
        console.error('Error updating deck popularity:', error);
        // Продолжаем выполнение, так как основная операция удаления уже выполнена
      }
      
      setSnackbar({
        open: true,
        message: t('messages.deckRemovedFromLibrary'),
        severity: 'success',
      });
      handleCloseRemoveDeckDialog();
    } catch (error) {
      console.error('Error removing deck from library:', error);
      setSnackbar({
        open: true,
        message: t('messages.failedToAddDeck'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!user) {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderDeckTable = (decks: ExtendedDeck[], isOwn: boolean) => {
    if (decks.length === 0) {
      return (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isOwn ? t('decks.noDecksYet') : t('decks.noAddedDecks')}
          </Typography>
          <Typography color="text.secondary">
            {isOwn 
              ? t('decks.createYourFirstDeck')
              : t('decks.noAddedPublicDecksYet')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 800 }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '3fr 1fr 1fr 1fr',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.100',
              p: 1
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', px: 2 }}>
                {t('formLabels.title')}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', px: 2 }}>
                {t('decks.cards')}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', px: 2 }}>
                {t('decks.learned')}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', px: 2, textAlign: 'center' }}>
                {t('decks.actions')}
              </Typography>
            </Box>
            {decks.map(deck => (
              <Box 
                key={deck.id}
                sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '3fr 1fr 1fr 1fr',
                  borderBottom: 1,
                  borderColor: 'divider',
                  p: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => navigate(`/decks/${deck.id}`)}
              >
                <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {deck.title}
                      {deck.isPublic ? (
                        <Tooltip title={t('decks.publicDeckTooltip')}>
                          <PublicIcon fontSize="small" color="primary" sx={{ ml: 0.5, fontSize: '1rem' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('decks.privateDeckTooltip')}>
                          <LockIcon fontSize="small" color="action" sx={{ ml: 0.5, fontSize: '1rem' }} />
                        </Tooltip>
                      )}
                      {deck.isPublic && deck.stats && typeof deck.stats.popularity === 'number' && deck.stats.popularity > 0 && (
                        <Tooltip title={t('decks.popularity')}>
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                            <StarIcon sx={{ color: 'gold', fontSize: '0.9rem' }} />
                            <Typography variant="caption" sx={{ ml: 0.3 }}>
                              {deck.stats.popularity}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {deck.description}
                    </Typography>
                    {deck.fromClassName && (
                      <Typography variant="body2" color="primary" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                        {t('decks.deckTypes.fromClass')}: {deck.fromClassName}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                  {deck.stats.totalCards}
                </Typography>
                <Typography sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                  {deck.stats.cardsStudied}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/study/${deck.id}`);
                    }}
                    title={t('decks.study')}
                    color="primary"
                  >
                    <PlayArrowIcon />
                  </IconButton>
                  
                  {isOwn && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/edit-deck/${deck.id}`);
                      }}
                      title={t('common.edit')}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  
                  {isOwn ? (
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenDeleteDeckDialog(deck.id, deck.title, e)}
                      title={t('common.delete')}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          bgcolor: 'error.light',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  ) : deck.fromClassId ? (
                    <Tooltip title={t('decks.deckFromClassRemoveTooltip')}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenRemoveDeckDialog(deck.id, deck.title, deck.fromClassId, deck.fromClassName, e)}
                        sx={{
                          color: 'info.main',
                          '&:hover': {
                            bgcolor: 'info.light',
                          },
                        }}
                      >
                        <ClassIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenRemoveDeckDialog(deck.id, deck.title, deck.fromClassId, deck.fromClassName, e)}
                      title={t('decks.removeFromLibrary')}
                      sx={{
                        color: 'warning.main',
                        '&:hover': {
                          bgcolor: 'warning.light',
                        },
                      }}
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/user')}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('profile.title')}
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 'normal' }}>
              {t('decks.myDecks')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={!isMobile ? <AddIcon /> : undefined}
            onClick={() => navigate('/create-deck')}
          >
            {!isMobile ? t('decks.createDeck') : <AddIcon />}
          </Button>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3 }}
        >
          <Tab label={t('decks.myDecksWithCount', { count: ownDecks.length })} />
          <Tab label={t('decks.addedDecksWithCount', { count: addedDecks.length })} />
        </Tabs>

        {activeTab === 0 && (
          <>
            {renderDeckTable(ownDecks, true)}
          </>
        )}

        {activeTab === 1 && (
          <>
            {renderDeckTable(addedDecks, false)}
          </>
        )}

        <Dialog
          open={isDeleteDeckDialogOpen}
          onClose={handleCloseDeleteDeckDialog}
        >
          <DialogTitle>{t('decks.deleteDeck')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {t('decks.confirmDelete', { title: deckToDelete?.title })}
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
              {t('decks.deleteDeckWarning')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDeckDialog}>{t('common.cancel')}</Button>
            <Button 
              onClick={handleDeleteDeck} 
              variant="contained" 
              color="error"
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isRemoveDeckDialogOpen}
          onClose={handleCloseRemoveDeckDialog}
        >
          <DialogTitle>{t('decks.removeFromLibrary')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {t('decks.confirmRemoveFromLibrary', { title: deckToRemove?.title })}
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
              {t('decks.removeFromLibraryNote')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRemoveDeckDialog}>{t('common.cancel')}</Button>
            <Button 
              onClick={handleRemoveDeck} 
              variant="contained" 
              color="warning"
            >
              {t('common.remove')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default DeckLibraryPage; 